import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Scale, FileText } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { US_STATES, STATUS_STYLES } from "@/data/states";

export default function StatePage() {
  const { code } = useParams();
  const upper = (code || "").toUpperCase();
  const stateMeta = US_STATES.find((s) => s.code === upper);
  const [rec, setRec] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get(`/legislation/${upper}`).then((r) => setRec(r.data)).catch(() => setRec(null)).finally(() => setLoaded(true));
  }, [upper]);

  useSeo({
    title: `${stateMeta?.name || upper} Wolfdog Legislation`,
    description: rec
      ? `Wolfdog ownership laws in ${stateMeta?.name || upper}: ${rec.summary}`
      : `Wolfdog ownership laws, permit requirements, and restrictions in ${stateMeta?.name || upper}.`,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: (typeof window !== "undefined" ? window.location.origin : "") + "/" },
        { "@type": "ListItem", position: 2, name: "Legislation Center", item: (typeof window !== "undefined" ? window.location.origin : "") + "/legislation" },
        { "@type": "ListItem", position: 3, name: `${stateMeta?.name || upper} Legislation` },
      ],
    },
  });

  const status = rec?.status || "No Specific Regulation";

  return (
    <div className="max-w-4xl mx-auto px-4 py-14">
      <Link to="/legislation" className="overline text-[#8B6D1F] hover:underline inline-flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> All States
      </Link>

      <div className="flex items-center gap-3 mt-6">
        <Scale className="w-7 h-7 text-[#D4AF37]" />
        <span className="overline text-[#8B6D1F]">Legislation</span>
      </div>
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-[#0A192F] mt-2">
        {stateMeta?.name || upper}
      </h1>
      <span className={`inline-block mt-4 px-4 py-2 border overline rounded-full ${STATUS_STYLES[status]}`}>
        {status}
      </span>

      <div className="mt-10 bg-white border border-[#0A192F]/10 p-8">
        {loaded && rec ? (
          <>
            <h2 className="font-display text-xl font-bold text-[#0A192F] mb-3">Summary</h2>
            <p className="text-lg text-[#1a2b45] leading-relaxed">{rec.summary}</p>
            {rec.details && (
              <>
                <h2 className="font-display text-xl font-bold text-[#0A192F] mt-8 mb-3">Details</h2>
                <p className="text-[#1a2b45] leading-relaxed whitespace-pre-line">{rec.details}</p>
              </>
            )}
            {rec.bills?.length > 0 && (
              <>
                <h2 className="font-display text-xl font-bold text-[#0A192F] mt-8 mb-3">Relevant Bills &amp; Statutes</h2>
                <ul className="space-y-2">
                  {rec.bills.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-[#1a2b45]">
                      <FileText className="w-4 h-4 text-[#D4AF37] mt-1 shrink-0" /> {b}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        ) : (
          <div>
            <p className="text-lg text-[#1a2b45] leading-relaxed">
              We do not yet have a detailed legislation record for {stateMeta?.name || upper}. This state currently has
              no specific wolfdog regulation tracked in our database, or the record is being researched.
            </p>
            <p className="mt-4 text-[#4A5568]">
              Always verify with local county and municipal ordinances, which can differ from state law.
            </p>
          </div>
        )}
        <p className="mt-8 text-xs text-[#4A5568] border-t border-[#0A192F]/10 pt-4">
          This information is provided for educational purposes and is not legal advice. Laws change frequently — confirm
          current regulations with official state and local sources.
        </p>
      </div>
    </div>
  );
}
